import { useState, useRef, useEffect } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import ScrollAnimation from '../animations/ScrollAnimation'
import InteractiveCard from '../animations/InteractiveCard'
import ParallaxCard from '../animations/ParallaxCard'
import { getProjects } from '../../services/api'
import type { Project } from '../../types/database'
import FluidBackground from '../animations/FluidBackground'

const transition = { duration: 0.6, ease: [0.25, 1, 0.5, 1] }
const springConfig = { type: "spring", stiffness: 60, damping: 10 }


export default function Projects() {
  const [selectedTag, setSelectedTag] = useState<string>('All')
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  useEffect(() => {
    loadProjects()
  }, [])

  const loadProjects = async () => {
    setLoading(true)
    const data = await getProjects()
    setProjects(data)
    setLoading(false)
  }

  const allTags = Array.from(
    new Set(projects.flatMap((p) => p.tags || []))
  )

  const filteredProjects =
    selectedTag === 'All'
      ? projects
      : projects.filter((p) => (p.tags || []).includes(selectedTag))

  return (
    <section
      id="projects"
      ref={ref}
      className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-background"
    >
      <div className="container mx-auto max-w-7xl relative z-10">
        <ScrollAnimation variant="slideUp" delay={0.2} className="text-center mb-16">
          <motion.span
            className="inline-block px-4 py-2 text-xs font-medium text-accent mb-6 uppercase tracking-widest"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
          >
            My Work
          </motion.span>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold mb-6 font-display leading-[1.1] tracking-tight">
            <span className="text-gradient-glow">Featured</span>{' '}
            <span className="text-text-primary">Projects</span>
          </h2>
          <p className="text-base sm:text-lg text-text-primary/70 max-w-xl mx-auto leading-relaxed mb-16">
            A collection of innovative projects showcasing technical expertise and
            creative problem-solving.
          </p>
        </ScrollAnimation>

        {/* Filter Tags */}
        {allTags.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {['All', ...allTags].map((tag) => (
            <motion.button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              data-magnetic
              className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                selectedTag === tag
                  ? 'bg-accent text-text-primary-dark shadow-sm'
                  : 'neu text-text-primary/70 hover:text-text-primary'
              }`}
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              <span className="relative z-10">{tag}</span>
            </motion.button>
            ))}
          </motion.div>
        )}

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center text-text-primary/50 py-12">Loading projects...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredProjects.map((project, index) => (
              <ParallaxCard key={project.id} intensity={0.3}>
                <motion.div
                  layout
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ 
                    opacity: 0, 
                    scale: 0.3,
                    y: -10,
                  }}
                  transition={{
                    duration: 0.4,
                    ease: [0.25, 1, 0.5, 1],
                    delay: index * 0.02,
                  }}
                >
                <InteractiveCard
                  intensity={8}
                  className="neu p-6 group cursor-pointer neu-hover rounded-2xl"
                  onClick={() => setSelectedProject(project)}
                >
              <div className="w-full h-full">
                <div className="relative h-48 rounded-2xl overflow-hidden mb-4">
                  <FluidBackground intensity={0.3} />
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-accent-secondary/10 to-accent/5" />
                  <div className="absolute inset-0 bg-background/60 group-hover:bg-background/40 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl text-text-primary/50 group-hover:text-accent transition-colors font-semibold">
                      {project.title.charAt(0)}
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-text-primary group-hover:text-accent transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-text-primary/70 text-sm mb-4 line-clamp-2">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 neu text-xs text-text-primary/70 font-medium rounded-lg"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-4">
                    {project.project_url && (
                      <a
                        href={project.project_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-accent hover:text-accent-secondary text-sm font-medium transition-colors"
                      >
                        View →
                      </a>
                    )}
                    {project.github_url && (
                      <a
                        href={project.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-text-primary/70 hover:text-text-primary text-sm font-medium transition-colors"
                      >
                        GitHub →
                      </a>
                    )}
                  </div>
                </div>
              </div>
              </InteractiveCard>
              </motion.div>
              </ParallaxCard>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-md"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={transition}
              onClick={(e) => e.stopPropagation()}
              className="neu p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 text-text-primary/50 hover:text-text-primary text-2xl transition-colors neu p-2"
              >
                ×
              </button>
              <h3 className="text-3xl font-semibold mb-4 text-text-primary">
                {selectedProject.title}
              </h3>
              <p className="text-text-primary/70 mb-6">{selectedProject.description}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedProject.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-2 neu text-sm text-text-primary/70 rounded-lg"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex gap-4">
                {selectedProject.project_url && (
                  <a
                    href={selectedProject.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative px-6 py-3 rounded-2xl font-medium text-black hover:shadow-lg transition-all overflow-hidden"
                  >
                    <div className="absolute inset-0 rounded-2xl overflow-hidden">
                      <FluidBackground intensity={0.9} />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-accent to-accent-secondary opacity-75" />
                    <span className="relative z-10">View Project</span>
                  </a>
                )}
                {selectedProject.github_url && (
                  <a
                    href={selectedProject.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 neu font-medium text-text-primary hover:text-accent transition-all"
                  >
                    View Code
                  </a>
                )}
              </div>
              {selectedProject.long_description && (
                <p className="text-text-primary/70 mt-6 leading-relaxed">
                  {selectedProject.long_description}
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
